// The Roles page's parts: a role's permission matrix, grouped by range (D51) in the style of
// Isomorphic's roles-permissions page, and the form that gives a user a role.
import { CheckSquare } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Button, Checkbox } from "rizzui";

import { can, type Me } from "../auth/access";
import { permissionsQuery, type Role, setRolePermissions, setUserRole, usersQuery } from "../auth/admin";
import { useT } from "../i18n";
import { toast } from "../layout/toast";
import { cn } from "../ui/cn";
import { SelectField } from "../ui/SideDrawer";
import { ErrorState, Loading } from "../ui/states";

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function Matrix({ role, me, onSaved }: { role: Role; me: Me; onSaved: () => Promise<void> }) {
  const t = useT().users.roles;
  const groups = useQuery({ ...permissionsQuery(), enabled: can(me, "ViewPermissions") });
  const [chosen, setChosen] = useState(() => new Set(role.permissions));
  const editable = can(me, "UpdateRolePermissions") && !role.is_super_admin;
  if (groups.isError) return <ErrorState message={groups.error.message} />;
  if (!groups.data) return <Loading />;
  const held = (name: string) => role.is_super_admin || chosen.has(name);

  function flip(name: string) {
    const next = new Set(chosen);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setChosen(next);
  }

  async function save() {
    try {
      await setRolePermissions(role.id, [...chosen]);
      toast(t.saved(role.name));
      await onSaved();
    } catch (e) {
      toast(message(e), "warn");
    }
  }

  return (
    <div className="space-y-4">
      {role.is_super_admin ? (
        <p className="m-0 rounded-lg bg-primary-lighter/60 px-3 py-2 text-[13px] text-primary-dark">
          {t.superAdminNote}
        </p>
      ) : null}
      <div className="grid gap-4 2xl:grid-cols-2">
        {groups.data.map((g) => {
          const count = g.permissions.filter((p) => held(p.name)).length;
          return (
            <fieldset key={g.group} aria-label={g.group} className="m-0 rounded-xl border border-muted p-4">
              <legend className="sr-only">{g.group}</legend>
              <div className="mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <CheckSquare weight="duotone" className="h-4 w-4 text-primary" aria-hidden /> {g.group}
                </span>
                <span className={cn("tabular text-xs", count === g.permissions.length ? "text-green-dark" : "text-gray-500")}>
                  {t.held(count, g.permissions.length)}
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {g.permissions.map((p) => (
                  <Checkbox
                    key={p.name}
                    title={p.description}
                    checked={held(p.name)}
                    disabled={!editable}
                    onChange={() => flip(p.name)}
                    label={
                      <span>
                        <span className="font-mono text-[12.5px] font-medium text-gray-900">{p.name}</span>
                        <span className="block text-xs text-gray-500">{p.description}</span>
                      </span>
                    }
                  />
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
      {editable ? <Button onClick={() => void save()}>{t.save}</Button> : null}
    </div>
  );
}

export function Assign({ roles }: { roles: Role[] }) {
  const t = useT().users.roles;
  const users = useQuery(usersQuery());
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState("");
  const [roleId, setRoleId] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await setUserRole(userId, roleId);
      toast(t.assigned);
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (err) {
      toast(message(err), "warn");
    }
  }
  return (
    <form className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]" aria-label={t.assignForm} onSubmit={(e) => void submit(e)}>
      <SelectField
        label={t.user}
        value={userId}
        onChange={setUserId}
        options={[{ value: "", label: t.chooseUser }, ...(users.data ?? []).map((u) => ({ value: u.id, label: `${u.display_name} (${u.role})` }))]}
      />
      <SelectField label={t.role} value={roleId} onChange={setRoleId} options={[{ value: "", label: t.chooseRole }, ...roles.map((r) => ({ value: r.id, label: r.name }))]} />
      <Button type="submit" disabled={!userId || !roleId}>
        {t.assign}
      </Button>
    </form>
  );
}
